import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldCheck, MapPin, Loader2, ArrowLeft, Home, Heart, Bed, Bath, ChevronDown, ChevronUp, Maximize2, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useChat, ChatMessage } from '../../hooks/useChat';
import { useMatches } from '../../hooks/useMatches';
import { useUser } from '../../hooks/useUsers';
import { usePropertyRecommendations } from '../../hooks/usePropertyRecommendations';
import { calculateCompatibilityScore } from '../../utils/compatibility';
import BottomNav from '../../components/BottomNav';
import TenantNav from '../../components/TenantNav';
import { BrowseTogetherModal } from '../../components/BrowseTogetherModal';
import { usePropertyInteraction } from '../../hooks/usePropertyInteraction';
import { ChatBubble, SystemMessage, ChatInput } from '../../components/ui';
import { Property } from '../../types/property.types';
import { PropertyActions } from '../../components/PropertyActions';
import { InquiryForm } from '../../components/InquiryForm';
import { markChatRead, sendPropertyMessage } from '../../services/secureOperations';
import { generateIcebreakers } from '../../services/ai';

const Chat: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { user: currentUserData } = useUser(currentUser?.uid);
  const { matches, loading: matchesLoading } = useMatches();
  const { messages, sendMessage, loading: messagesLoading } = useChat(matchId);
  const [draft, setDraft] = useState('');
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [icebreakers, setIcebreakers] = useState<string[]>([]);
  const [loadingIcebreakers, setLoadingIcebreakers] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Find the current match and matched user
  const currentMatch = matches.find(m => m.id === matchId);
  const matchedUserId = currentMatch?.users.find((uid: string) => uid !== currentUser?.uid);
  const { user: matchedUser, loading: userLoading } = useUser(matchedUserId);
  
  // Get property recommendations
  const { properties, loading: propertiesLoading } = usePropertyRecommendations({
    match: currentMatch,
    maxResults: 20,
    includeViewed: true,
  });
  
  const compatibilityScore = currentUserData && matchedUser 
    ? calculateCompatibilityScore(currentUserData, matchedUser)
    : 0;

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Mark messages as read and reset unread count when viewing chat
  useEffect(() => {
    const markAsRead = async () => {
      if (!matchId || !currentUser?.uid || !currentMatch) return;
      
      try {
        const unreadCount = currentMatch.unreadCount?.[currentUser.uid] || 0;
        if (unreadCount > 0) await markChatRead(matchId);
      } catch (error) {
        console.error('Error marking messages as read:', error);
      }
    };

    void markAsRead();
  }, [matchId, currentUser?.uid, currentMatch, messages.length]);

  // AI conversation openers (Firebase AI Logic / Gemini).
  const loadIcebreakers = async () => {
    setLoadingIcebreakers(true);
    try {
      const openers = await generateIcebreakers(currentUserData, matchedUser, {
        city: currentMatch?.sharedCity,
        lifestyle: currentMatch?.sharedLifestyle,
      });
      setIcebreakers(openers);
    } finally {
      setLoadingIcebreakers(false);
    }
  };

  const handleSend = async () => {
    if (!draft.trim() || !matchId || !matchedUserId) return;

    try {
      await sendMessage(draft.trim(), matchId, matchedUserId);
      setDraft('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const sendPropertyCard = async (property: Property) => {
    if (!matchId || !currentUser || !currentMatch) return;

    try {
      await sendPropertyMessage(matchId, property.id);
    } catch (error) {
      console.error('Error sending property card:', error);
    }
  };

  const handlePropertyClick = async (property: Property) => {
    // Send property card to chat
    await sendPropertyCard(property);
    
    // Close the modal
    setShowBrowseModal(false);
  };

  if (matchesLoading || messagesLoading || userLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-4 pt-4 pb-24 md:pb-10">
        <div className="max-w-6xl mx-auto space-y-4">
          <TenantNav />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-primary-300 animate-spin mx-auto mb-4" />
              <p className="text-text-secondary font-semibold">Loading chat...</p>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  if (!matchedUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-4 pt-4 pb-24 md:pb-10">
        <div className="max-w-6xl mx-auto space-y-4">
          <TenantNav />
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="card p-6 md:p-8 text-center max-w-md">
              <Heart className="w-12 h-12 md:w-16 md:h-16 text-primary-300 mx-auto mb-4" />
              <h2 className="text-xl md:text-2xl font-bold text-text-primary mb-2">Match not found</h2>
              <p className="text-text-secondary mb-6 text-sm md:text-base">
                This match may no longer be available.
              </p>
              <button className="btn-primary" onClick={() => navigate('/tenant/matches')}>
                Back to Matches
              </button>
            </div>
          </div>
        </div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 flex flex-col">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-28 -left-20 w-72 h-72 bg-primary-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute bottom-12 right-8 w-64 h-64 bg-accent-purple rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative max-w-6xl mx-auto w-full px-3 md:px-4 pt-4 flex flex-col flex-1 pb-44 md:pb-36">
        <TenantNav />

      {/* Header - sticky on mobile */}
      <div className="relative bg-white/80 backdrop-blur-sm border-b-2 border-gray-100 px-2 sm:px-4 py-3 md:py-4 mt-4">
        <div className="max-w-6xl mx-auto flex items-center gap-3 md:gap-4">
          <button
            onClick={() => navigate('/tenant/matches')}
            className="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-text-primary" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg sm:text-xl md:text-2xl font-extrabold text-text-primary truncate">
                {matchedUser.name}
              </h2>
              <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-full bg-primary-50 text-primary-300 text-[10px] md:text-xs font-semibold whitespace-nowrap">
                {compatibilityScore}% match
              </span>
            </div>
            <p className="text-xs md:text-sm text-text-secondary flex items-center gap-1.5 mt-0.5">
              <MapPin className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
              {matchedUser.city} · R{matchedUser.budgetMin ? Math.floor(matchedUser.budgetMin/1000) : 0}k-{matchedUser.budgetMax ? Math.floor(matchedUser.budgetMax/1000) : 0}k share
            </p>
            
            {/* Quick info badges */}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {matchedUser.lifestyle && matchedUser.lifestyle.length > 0 && (
                <span className="px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-700 text-[10px] font-medium flex items-center gap-1">
                  <span className="text-xs">🏃</span>
                  {matchedUser.lifestyle[0]}
                </span>
              )}
              {matchedUser.smokingPreference && (
                <span className="px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-700 text-[10px] font-medium">
                  {{
                    no: '🚭 Non-smoking',
                    'outside-only': '🚬 Smoking outside only',
                    yes: '🚬 Smoking OK',
                    negotiable: '🚬 Smoking negotiable',
                  }[matchedUser.smokingPreference]}
                </span>
              )}
              {matchedUser.petsPreference && (
                <span className="px-2 py-0.5 rounded-md bg-white border border-gray-200 text-gray-700 text-[10px] font-medium">
                  {{
                    yes: '🐾 Pets OK',
                    no: '🚫 No pets',
                    negotiable: '🐾 Pets negotiable',
                  }[matchedUser.petsPreference]}
                </span>
              )}
            </div>
          </div>
          <div className="hidden sm:flex gap-2">
            <button
              onClick={() => setShowBrowseModal(true)}
              className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-xl font-semibold border-2 transition-all text-xs md:text-sm shadow-lg bg-gradient-to-r from-primary-200 to-primary-300 text-white border-primary-300 hover:from-primary-300 hover:to-primary-400"
            >
              <Home className="w-3 h-3 md:w-4 md:h-4" />
              Browse Together ({properties.length})
            </button>
          </div>
          {matchedUser.profileComplete && (
            <div className="hidden sm:flex px-3 md:px-4 py-1.5 md:py-2 rounded-xl bg-primary-50 text-primary-300 font-semibold border border-primary-100 text-xs md:text-sm">
              <ShieldCheck className="w-3 h-3 md:w-4 md:h-4 inline mr-1.5" /> Complete profile
            </div>
          )}
        </div>
      </div>

      {/* Messages area - scrollable */}
      <div className="relative flex-1 overflow-y-auto px-2 sm:px-4 py-4 md:py-6">
        <div className="max-w-4xl mx-auto space-y-3 md:space-y-4">
          
          {messages.length === 0 ? (
            <div className="text-center py-8 md:py-12">
              <Heart className="w-12 h-12 md:w-16 md:h-16 text-primary-300 mx-auto mb-4" />
              <h3 className="text-lg md:text-xl font-bold text-text-primary mb-2">Start the conversation!</h3>
              <p className="text-text-secondary text-sm md:text-base">
                Say hello to {matchedUser.name} and start planning your roommate journey.
              </p>

              {/* AI conversation openers (Firebase AI Logic / Gemini) */}
              <div className="mt-6 max-w-md mx-auto">
                {icebreakers.length === 0 ? (
                  <button
                    type="button"
                    onClick={loadIcebreakers}
                    disabled={loadingIcebreakers}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border-2 border-primary-100 text-primary-300 font-semibold hover:border-primary-200 transition-all shadow-sm disabled:opacity-60"
                  >
                    {loadingIcebreakers ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {loadingIcebreakers ? 'Thinking of openers…' : 'Break the ice ✨'}
                  </button>
                ) : (
                  <div className="space-y-2 text-left">
                    <div className="flex items-center justify-between px-1">
                      <p className="text-xs font-semibold text-text-secondary flex items-center gap-1">
                        <Sparkles className="w-3.5 h-3.5 text-primary-300" /> Tap an opener to use it
                      </p>
                      <button
                        type="button"
                        onClick={loadIcebreakers}
                        disabled={loadingIcebreakers}
                        className="text-xs text-primary-300 font-semibold hover:underline disabled:opacity-60"
                      >
                        {loadingIcebreakers ? '…' : 'Refresh'}
                      </button>
                    </div>
                    {icebreakers.map((line) => (
                      <button
                        key={line}
                        type="button"
                        onClick={() => setDraft(line)}
                        className="block w-full text-left px-4 py-2.5 rounded-xl bg-white border border-primary-100 text-sm text-text-primary hover:border-primary-200 hover:bg-primary-50 transition-all"
                      >
                        {line}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowBrowseModal(true)}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-primary-200 to-primary-300 text-white font-semibold hover:from-primary-300 hover:to-primary-400 transition-all shadow-lg"
              >
                <Home className="w-5 h-5" />
                🏠 Browse {properties.length} Properties Together
              </button>
            </div>
          ) : (
            messages.map((message) => {
              if (
                message.type === 'mutual_like' ||
                message.type === 'property_like' ||
                message.type === 'property_liked_notification' ||
                message.type === 'inquiry_request' ||
                message.type === 'inquiry_sent'
              ) {
                return (
                  <PropertyInteractionMessage
                    key={message.id}
                    message={message as ChatMessage}
                    matchId={matchId || ''}
                    partnerId={matchedUserId}
                    partnerName={matchedUser.name}
                    currentUserId={currentUser?.uid}
                  />
                );
              }

              const isMe = message.senderId === currentUser?.uid;
              const timestamp = message.timestamp 
                ? new Date(message.timestamp).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                  })
                : 'Now';

              return (
                <ChatBubble
                  key={message.id}
                  message={message.text}
                  timestamp={timestamp}
                  isOwn={isMe}
                  type="text"
                />
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      </div>

      {/* Input area - sticky at bottom */}
      <div className="fixed bottom-16 md:bottom-0 left-0 right-0 bg-white border-t-2 border-gray-100 px-2 sm:px-4 py-3 md:py-4 z-50 shadow-lg">
        {/* Mobile property banner - only on small screens */}
        {properties.length > 0 && (
          <div className="sm:hidden mb-2 p-3 bg-gradient-to-r from-primary-200 to-primary-300 rounded-lg shadow-lg">
            <button
              onClick={() => setShowBrowseModal(true)}
              className="w-full flex items-center justify-between text-sm font-bold text-white"
            >
              <span className="flex items-center gap-2">
                <Home className="w-5 h-5" />
                🏠 {properties.length} Properties Available
              </span>
              <span className="text-xs bg-white/20 px-3 py-1 rounded-full">Browse →</span>
            </button>
          </div>
        )}
        
        <div className="max-w-4xl mx-auto">
          <ChatInput
            value={draft}
            onChange={setDraft}
            onSend={handleSend}
            leftButton={{
              icon: <Home className="w-5 h-5" />,
              onClick: () => setShowBrowseModal(true),
              active: showBrowseModal,
            }}
          />
        </div>
      </div>

      {/* Browse Together Modal */}
      {currentMatch && (
        <BrowseTogetherModal
          isOpen={showBrowseModal}
          onClose={() => setShowBrowseModal(false)}
          properties={properties}
          match={currentMatch}
          onPropertyClick={handlePropertyClick}
          loading={propertiesLoading}
        />
      )}

      <BottomNav />
    </div>
  );
};

interface PropertyInteractionMessageProps {
  message: ChatMessage;
  matchId: string;
  partnerId?: string;
  partnerName?: string;
  currentUserId?: string;
}

function PropertyInteractionMessage({
  message,
  matchId,
  partnerId,
  partnerName,
  currentUserId,
}: PropertyInteractionMessageProps) {
  const [confirming, setConfirming] = useState(false);
  const { confirmInquiry } = usePropertyInteraction({
    matchId,
    propertyId: message.propertyId || '',
    propertyTitle: message.propertyTitle || message.text || '',
    partnerUserId: partnerId,
    partnerName,
  });

  const handleConfirm = async () => {
    if (!message.propertyId) return;
    setConfirming(true);
    try {
      await confirmInquiry();
    } catch (error) {
      console.error('Error confirming inquiry from chat:', error);
    } finally {
      setConfirming(false);
    }
  };

  if (message.type === 'mutual_like') {
    return (
      <SystemMessage
        type="mutual_like"
        message={message.text}
      />
    );
  }

  if (message.type === 'property_liked_notification') {
    const isOwn = message.senderId === currentUserId;
    const senderName = isOwn ? 'You' : (partnerName || 'Your match');
    return (
      <SystemMessage
        type="property_like"
        message={`${senderName} ${message.text}`}
      />
    );
  }

  if (message.type === 'property_like') {
    return (
      <ExpandablePropertyCard
        message={message}
        currentUserId={currentUserId}
        matchId={matchId}
        partnerId={partnerId}
        partnerName={partnerName}
      />
    );
  }

  if (message.type === 'inquiry_request') {
    const canConfirm = message.senderId !== currentUserId && !!message.propertyId;
    return (
      <SystemMessage
        type="inquiry_request"
        message={message.text}
        secondaryMessage={message.inquiryMessage ? `"${message.inquiryMessage}"` : undefined}
        action={
          canConfirm
            ? {
                label: 'Confirm Inquiry',
                onClick: handleConfirm,
                loading: confirming,
              }
            : undefined
        }
      />
    );
  }

  if (message.type === 'inquiry_sent') {
    return <SystemMessage type="inquiry_sent" message={message.text} />;
  }

  return null;
}

interface ExpandablePropertyCardProps {
  message: ChatMessage;
  currentUserId?: string;
  matchId: string;
  partnerId?: string;
  partnerName?: string;
}

function ExpandablePropertyCard({
  message,
  currentUserId,
  matchId,
  partnerId,
  partnerName,
}: ExpandablePropertyCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryMessage, setInquiryMessage] = useState('');

  const isOwn = message.senderId === currentUserId;
  const propertyData = (message as any).propertyData;

  const {
    likeProperty,
    initiateInquiry,
    userHasLiked,
    canSendInquiry,
    inquiryStatus,
    interaction,
  } = usePropertyInteraction({
    matchId,
    propertyId: message.propertyId || '',
    propertyTitle: message.propertyTitle || propertyData?.title || '',
    partnerUserId: partnerId,
    partnerName,
  });

  const handleLike = async () => {
    if (userHasLiked || actionLoading) return;
    setActionLoading('like');
    try {
      await likeProperty();
    } catch (error) {
      console.error('Error liking property:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleInitiateInquiry = () => {
    setShowInquiryForm(true);
  };

  const handleSendInquiry = async () => {
    if (actionLoading) return;
    setActionLoading('inquiry');
    try {
      await initiateInquiry(inquiryMessage);
      setShowInquiryForm(false);
      setInquiryMessage('');
    } catch (error) {
      console.error('Error sending inquiry:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelInquiry = () => {
    setShowInquiryForm(false);
    setInquiryMessage('');
  };

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`w-full max-w-md sm:max-w-lg ${isOwn ? 'items-end' : 'items-start'} flex flex-col gap-2`}>
        <p className="text-xs text-text-secondary px-2">
          {message.senderName || (isOwn ? 'You' : 'Your match')} shared a property
        </p>
        
        <div className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl w-full">
          {/* Collapsed View - Always Visible */}
          <div
            onClick={() => setIsExpanded(!isExpanded)}
            className="cursor-pointer"
          >
            {propertyData?.primaryPhotoUrl ? (
              <div className={`w-full overflow-hidden relative transition-all duration-300 ${isExpanded ? 'h-48 sm:h-56' : 'h-40'}`}>
                <img
                  src={propertyData.primaryPhotoUrl}
                  alt={propertyData.title}
                  className="w-full h-full object-cover"
                />
                {isExpanded && propertyData.matchScore && (
                  <div className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-md">
                    <span className="text-sm font-bold text-primary-300">{propertyData.matchScore}% Match</span>
                  </div>
                )}
                <div className="absolute top-3 right-3 bg-primary-300 text-white text-sm font-bold px-3 py-1.5 rounded-lg shadow-lg">
                  R{propertyData.monthlyRent?.toLocaleString()}/mo
                </div>
                {!isExpanded && (
                  <div className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-md">
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  </div>
                )}
              </div>
            ) : (
              <div className={`w-full bg-gradient-to-br from-primary-100 via-purple-100 to-blue-100 flex items-center justify-center transition-all duration-300 ${isExpanded ? 'h-48 sm:h-56' : 'h-40'}`}>
                <Home className="w-12 h-12 text-primary-300/30" />
              </div>
            )}

            {/* Compact Info - Always Visible */}
            <div className="p-3 sm:p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-bold text-sm sm:text-base text-text-primary flex-1">
                  {propertyData?.title || message.propertyTitle || 'Property'}
                </h3>
                {isExpanded && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsExpanded(false);
                    }}
                    className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <ChevronUp className="w-4 h-4 text-gray-600" />
                  </button>
                )}
              </div>

              {!isExpanded && propertyData?.address && (
                <p className="text-xs text-text-secondary flex items-center gap-1 mt-1">
                  <MapPin className="w-3 h-3" />
                  {typeof propertyData.address === 'string' 
                    ? propertyData.address 
                    : `${propertyData.address.suburb ? propertyData.address.suburb + ', ' : ''}${propertyData.address.city}`}
                </p>
              )}

              {!isExpanded && (propertyData?.bedrooms || propertyData?.bathrooms) && (
                <div className="flex gap-3 text-xs text-text-secondary mt-2">
                  {propertyData.bedrooms && (
                    <span className="flex items-center gap-1">
                      <Bed className="w-3 h-3" />
                      {propertyData.bedrooms}
                    </span>
                  )}
                  {propertyData.bathrooms && (
                    <span className="flex items-center gap-1">
                      <Bath className="w-3 h-3" />
                      {propertyData.bathrooms}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="px-3 sm:px-4 pb-3 sm:pb-4 space-y-3 sm:space-y-4">
              {/* Full Address */}
              {propertyData?.address && (
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <MapPin className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {typeof propertyData.address === 'string' 
                      ? propertyData.address 
                      : `${propertyData.address.suburb ? propertyData.address.suburb + ', ' : ''}${propertyData.address.city}`}
                  </span>
                </div>
              )}

              {/* Property Details */}
              <div className="flex items-center gap-4 text-sm">
                {propertyData?.bedrooms && (
                  <div className="flex items-center gap-1 text-text-secondary">
                    <Bed className="w-4 h-4" />
                    <span>{propertyData.bedrooms} bed</span>
                  </div>
                )}
                {propertyData?.bathrooms && (
                  <div className="flex items-center gap-1 text-text-secondary">
                    <Bath className="w-4 h-4" />
                    <span>{propertyData.bathrooms} bath</span>
                  </div>
                )}
                {propertyData?.squareMeters && (
                  <div className="flex items-center gap-1 text-text-secondary">
                    <Maximize2 className="w-4 h-4" />
                    <span>{propertyData.squareMeters}m²</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {propertyData?.description && (
                <p className="text-sm text-text-secondary leading-relaxed">
                  {propertyData.description}
                </p>
              )}

              {/* Like Status */}
              {userHasLiked && (
                <div className="bg-pink-50 border border-pink-100 rounded-lg p-2 sm:p-3">
                  <p className="text-xs sm:text-sm text-pink-600 font-medium flex items-center gap-1.5">
                    <Heart className="w-4 h-4 fill-current" />
                    You liked this property
                  </p>
                </div>
              )}

              {/* Inquiry Status Badge */}
              {inquiryStatus && (
                <div className={`rounded-lg p-2 sm:p-3 ${
                  inquiryStatus === 'pending_confirmation' 
                    ? 'bg-yellow-50 border border-yellow-100' 
                    : inquiryStatus === 'sent' 
                    ? 'bg-green-50 border border-green-100' 
                    : 'bg-blue-50 border border-blue-100'
                }`}>
                  <p className={`text-xs sm:text-sm font-medium flex items-center gap-1.5 ${
                    inquiryStatus === 'pending_confirmation' 
                      ? 'text-yellow-700' 
                      : inquiryStatus === 'sent' 
                      ? 'text-green-700' 
                      : 'text-blue-700'
                  }`}>
                    {inquiryStatus === 'pending_confirmation' && '⏳ Inquiry pending confirmation'}
                    {inquiryStatus === 'sent' && '✅ Inquiry sent to landlord'}
                    {inquiryStatus === 'confirmed' && '✓ Inquiry confirmed'}
                  </p>
                </div>
              )}

              {/* Property Actions - hide if inquiry exists */}
              {!interaction?.inquiry && (
                <div onClick={(e) => e.stopPropagation()}>
                  <PropertyActions
                    userHasLiked={userHasLiked}
                    canSendInquiry={canSendInquiry || false}
                    actionLoading={actionLoading}
                    onLike={handleLike}
                    onInitiateInquiry={handleInitiateInquiry}
                  />
                </div>
              )}

              {/* Inquiry Form */}
              {showInquiryForm && (
                <div onClick={(e) => e.stopPropagation()}>
                  <InquiryForm
                    message={inquiryMessage}
                    onMessageChange={setInquiryMessage}
                    onSubmit={handleSendInquiry}
                    onCancel={handleCancelInquiry}
                    loading={actionLoading === 'inquiry'}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Chat;
